import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "src/lib/axios";
import { API_SERVICE_BACKEND_2, API_SERVICE_BACKEND } from "src/config";
import { useAuth } from "src/contexts/auth/firebase-context";

export const useUserDetail = () => {
  const queryClient = useQueryClient();

  const userDetail = queryClient.getQueryData([
    "user-detail",
    sessionStorage.getItem("userEmail"),
  ]);

  if (!userDetail) {
    return null;
  }

  // Check if new interface is enabled
  const isNewInterfaceEnabled = userDetail?.newDesignInterface;

  // Base subscription contains plan and feature information
  const userBaseSubscription =
    userDetail?.userSubscription?.subscriptions?.base;

  // Indicates if the account is a white-label parent user
  const isWhiteLabelUser = Boolean(userDetail?.isWhiteLabel);

  // Indicates if the user belongs to a white-label parent account
  const isWhiteLabelSubUser = Boolean(userDetail?.whiteLabelUserId);

  // Stripe-managed subscriptions are identified by planId suffix
  const isSubscriptionStripeManaged = Boolean(
    userBaseSubscription?.planId?.includes("_stripe")
  );

  // Helper to check if all required integrations are enabled
  const hasAllIntegrations = (features, integrations) =>
    integrations.every((key) => Boolean(features?.[key]));

  // Required integrations for showing the Integrations tab
  const REQUIRED_INTEGRATIONS = ["ZAPIER", "HUBSPOT", "ZOHO", "SALESFORCE"];

  // Subscription feature set
  const features = userBaseSubscription?.features;

  // Show integrations only if all required features are enabled
  const showIntegrations = Boolean(
    features && hasAllIntegrations(features, REQUIRED_INTEGRATIONS)
  );

  // Check if the user is a team member
  const isTeamMember = sessionStorage.getItem("teamMemberEmail") ? true : false;
  const role = sessionStorage.getItem("role");
  const hasTeamMemberLimitedAccess = isTeamMember && role !== "ADMIN";

  return {
    ...userDetail,
    isNewInterfaceEnabled,
    isWhiteLabelUser,
    isWhiteLabelSubUser,
    isSubscriptionStripeManaged,
    showIntegrations,
    isTeamMember,
    role,
    hasTeamMemberLimitedAccess,
  };
};

// ——————————————————————————
// Get Logged in User Detail
// ——————————————————————————
const getUserDetail = async (email) => {
  const res = await axios.get(`${API_SERVICE_BACKEND}/getuserdetail?email=${encodeURIComponent(email)}`);
  return res.data?.data;
};

export const useGetUserDetail = () => {
  const { isAuthenticated } = useAuth();
  const email = sessionStorage.getItem("userEmail");
  return useQuery({
    queryKey: ["user-detail", email],
    queryFn: () => getUserDetail(email),
    enabled: !!email && isAuthenticated, // Avoid 401: run only after /token API has run
  });
};

// ——————————————————————————
// Generate JWT Auth Token
// ——————————————————————————
const generateJWTToken = async (payload) => {
  const res = await axios.post(`${API_SERVICE_BACKEND_2}/getJWTAuthToken`, {
    payload,
  });
  return res.data.token;
};

export const useGenerateJWTToken = () => {
  return useMutation({
    mutationFn: (payload) => generateJWTToken(payload),
  });
};

// ——————————————————————————
// Get User Accepted Invitation
// ——————————————————————————
const getUserActiveWorkspaces = async (email) => {
  const res = await axios.get(
    `${API_SERVICE_BACKEND}/user-accepted-invites/${email}`
  );
  return res.data.workspaces;
};

export const useGetUserAcceptedInvitation = () => {
  return useMutation({
    mutationFn: (email) => getUserActiveWorkspaces(email),
  });
};
